import { Signal, computed, signal } from '@angular/core';

export type AutoOrientStatus = 'queued' | 'running' | 'done' | 'error';

/** brepsmith's orient answer, in the UCS-turned frame the editor shows. */
export interface AutoOrientResult {
  up: [number, number, number];
  /** 3x3, row major; maps `up` onto +Z. */
  rotationMatrix: number[];
  critAngleDeg: number;
  supportAreaBeforeMm2: number;
  supportAreaAfterMm2: number;
  totalAreaMm2: number;
  improvementPct: number;
  sourceFile: string;
}

export interface AutoOrient {
  status: AutoOrientStatus;
  critAngleDeg: number;
  result?: AutoOrientResult;
  error?: string;
  started_at?: string;
  finished_at?: string;
  optimization_run_token?: string;
}

export const AUTO_ORIENT_DEFAULT_ANGLE_DEG = 45;

/**
 * The editor offers a retry once a job has been queued or running this long. Matches the
 * window after which the backend and desktop accept a restart of an in-flight job.
 */
export const AUTO_ORIENT_RETRY_AFTER_MS = 5 * 60 * 1000;

/** Overhang angle from vertical: strictly between 0 and 90, at most one decimal. */
export function isValidCritAngle(value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0 || value >= 90) return false;
  return Math.abs(value * 10 - Math.round(value * 10)) < 1e-9;
}

const RANK: Record<AutoOrientStatus, number> = { queued: 1, running: 2, done: 3, error: 3 };

/**
 * Per-project auto-orient state shared by the cloud and desktop services.
 *
 * Jobs are ordered by `started_at`: a frame from an older job never replaces a
 * newer one, and within one job the status only moves forward. While a request
 * is pending, only a job that started after the one it replaces is accepted.
 */
export class AutoOrientStatusStore {
  private readonly jobs = signal<ReadonlyMap<string, AutoOrient>>(new Map());
  /** Per id with a request in flight: floor timestamp and the job replaced by the first request. */
  private readonly pending = new Map<string, { floor: number; replaced: AutoOrient | null }>();

  statusFor(id: string): Signal<AutoOrient | null> {
    return computed(() => this.jobs().get(id) ?? null);
  }

  get(id: string): AutoOrient | null {
    return this.jobs().get(id) ?? null;
  }

  /** The job stored on the project. Replaces anything held. */
  seed(id: string, job: AutoOrient | null): void {
    this.pending.delete(id);
    this.write(id, job);
  }

  /** Shows `queued` the instant the user asks; returns what it replaced. */
  markRequested(id: string, critAngleDeg: number): AutoOrient | null {
    const existing = this.pending.get(id);
    if (existing) {
      this.write(id, { status: 'queued', critAngleDeg });
      return existing.replaced;
    }
    const previous = this.get(id);
    const floor = previous?.started_at ? Date.parse(previous.started_at) : Number.NEGATIVE_INFINITY;
    this.pending.set(id, { floor, replaced: previous });
    this.write(id, { status: 'queued', critAngleDeg });
    return previous;
  }

  /** Undoes `markRequested` after a refused request, unless the server has answered since. */
  rollback(id: string, previous: AutoOrient | null): void {
    if (!this.pending.has(id)) return;
    this.pending.delete(id);
    this.write(id, previous);
  }

  /** A server answer: the HTTP response or a status event. */
  apply(id: string, incoming: AutoOrient): void {
    const incomingMs = incoming.started_at ? Date.parse(incoming.started_at) : Number.NaN;
    if (Number.isNaN(incomingMs)) return;

    const entry = this.pending.get(id);
    if (entry !== undefined) {
      if (incomingMs <= entry.floor) return;
      this.pending.delete(id);
      this.write(id, incoming);
      return;
    }

    const current = this.get(id);
    if (current?.started_at) {
      const currentMs = Date.parse(current.started_at);
      if (incomingMs < currentMs) return;
      if (incomingMs === currentMs && RANK[incoming.status] < RANK[current.status]) return;
    }
    this.write(id, incoming);
  }

  private write(id: string, job: AutoOrient | null): void {
    this.jobs.update((map) => {
      const next = new Map(map);
      if (job) next.set(id, job);
      else next.delete(id);
      return next;
    });
  }
}
